import { useQuery } from '@tanstack/react-query';
import apiClient from 'services/apiClient';

import { API } from '../utils/Helper';
const API_BASE = API || 'http://localhost:3009';
const DEFAULT_GAME_IMG = 'https://static.nanogames.io/assets/slots.42749df8.png';

/**
 * Returns the correct absolute image URL for a game.
 * - If the URL starts with /uploads/ it's a backend-served static file
 * - If it's a full http URL, use as-is
 * - Otherwise fall back to default image
 */
export const getGameImage = (game) => {
    let url = game?.custom_thumbnail_url || game?.thumbnail_url;
    const fallbackImg = '/assets/bcimages/Casino/BC_Originals.png'; // Better default
    if (!url) return fallbackImg;

    // Rewrite /assets/wsImages/ to /uploads/games/ since they come from backend uploads
    if (url.startsWith('/assets/wsImages/')) {
        url = url.replace('/assets/wsImages/', '/uploads/games/');
    }

    if (url.startsWith('/uploads/')) return `${API_BASE}${url}`;
    if (url.startsWith('/assets/')) return url; // Let frontend handle relative assets
    if (url.startsWith('http')) return url;
    return fallbackImg;
};

/**
 * Returns the provider logo URL.
 * Priority: logo_url field → /uploads/providers/{id}.png → null
 */
export const getProviderImage = (provider) => {
    if (provider?.logo_url) {
        if (provider.logo_url.startsWith('/uploads/')) return `${API_BASE}${provider.logo_url}`;
        if (provider.logo_url.startsWith('http')) return provider.logo_url;
    }
    // Fallback to /uploads/providers/{id}.png
    if (provider?.id) return `${API_BASE}/uploads/providers/${provider.id}.png`;
    return null;
};

export const useGameEngine = (params = {}) => {
    return useQuery({
        queryKey: ['gameEngineGames', params],
        queryFn: async () => {
            const queryParams = { ...params };
            if (queryParams.group) {
                queryParams.group_id = queryParams.group;
                delete queryParams.group;
            }
            if (queryParams.provider) {
                queryParams.provider_id = queryParams.provider;
                delete queryParams.provider;
            }
            const { data } = await apiClient.get('/api/game-engine/games', { params: queryParams });
            return data.data || [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes cache
    });
};

export const useActiveGroups = () => {
    return useQuery({
        queryKey: ['gameEngineGroups'],
        queryFn: async () => {
            const { data } = await apiClient.get('/api/game-engine/groups');
            return data.data || [];
        },
        staleTime: 10 * 60 * 1000, // 10 minutes cache
    });
};

export const useProviders = (params = {}) => {
    return useQuery({
        queryKey: ['gameEngineProviders', params],
        queryFn: async () => {
            const { data } = await apiClient.get('/api/game-engine/providers', { params });
            return data.data || [];
        },
        staleTime: 10 * 60 * 1000, // 10 minutes cache
    });
};

export const launchGameEngine = async (game_id, user_code, currency) => {
    const { data } = await apiClient.post('/api/game-engine/launch', { game_id, user_code, currency });
    return data;
};

export const getGameCurrencies = async (game_id) => {
    const { data } = await apiClient.get(`/api/game-engine/games/${game_id}/currencies`);
    return data; // returns { success, data: currencies[], game: {...} }
};
